import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const PRODUCT_CATEGORIES = [
  {
    title: "Utility Applications",
    subtitle: "Cleaners, Optimizers & System Tools",
    desc: "High-performance mobile and desktop utilities designed to enhance device performance, clean up storage, manage files, and optimize system resources. Our utility apps are built with a focus on speed, reliability, and intuitive user experience. Every product undergoes rigorous testing to ensure it delivers measurable value from day one.",
    icon: "ri-tools-line",
    highlights: ["System cleaners", "Storage optimizers", "File managers", "Battery savers", "RAM boosters", "App uninstallers"],
    image: "https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20a%20clean%20utility%20app%20interface%20with%20storage%20cleaner%20and%20optimizer%20tools%2C%20sleek%20device%20on%20warm%20minimal%20cream%20background%2C%20professional%20product%20photography%2C%20soft%20natural%20lighting%2C%20clean%20composition%2C%20warm%20amber%20and%20neutral%20tones%2C%20high%20detail%2C%20sophisticated%20tech%20aesthetic%2C%20no%20text%20on%20screen&width=900&height=675&seq=product-utility-v2&orientation=landscape",
  },
  {
    title: "AI-Powered Services",
    subtitle: "Smart Tools & Automation",
    desc: "We integrate artificial intelligence into utility applications — from smart photo organizers and AI-based duplicate finders to intelligent notification managers and predictive text tools. Our AI services leverage on-device and cloud-based models to deliver fast, privacy-conscious smart features that users love.",
    icon: "ri-robot-2-line",
    highlights: ["AI photo organizers", "Smart duplicate finders", "Predictive assistants", "Automation scripts", "Smart notifications", "AI chat assistants"],
    image: "https://storage.readdy-site.link/project_files/564b8566-d4d5-4635-bf3c-ab9b4b833508/4f8b90a2-083e-494a-adc8-419cb07b3bed_compressed_Screenshot-2026-07-13-at-15.14.42.webp",
  },
  {
    title: "Performance Tools",
    subtitle: "Speed & Optimization",
    desc: "Dedicated tools for monitoring and improving device performance — CPU and memory optimizers, network speed boosters, and real-time system health dashboards for power users. Built for those who demand peak performance from their devices.",
    icon: "ri-speed-up-line",
    highlights: ["CPU & RAM optimizers", "Network boosters", "System monitors", "Gaming mode tools", "Thermal management", "Startup managers"],
    image: "https://readdy.ai/api/search-image?query=High-performance%20digital%20speedometer%20and%20system%20optimization%20dashboard%20on%20modern%20dark%20interface%2C%20dynamic%20speed%20lines%20and%20performance%20metrics%2C%20warm%20amber%20and%20gold%20glowing%20accents%2C%20professional%20tech%20aesthetic%2C%20clean%20sophisticated%20design%2C%20high%20detail%2C%20corporate%20technology%20visualization%2C%20no%20text&width=900&height=675&seq=product-perf-v3&orientation=landscape",
  },
];

const APPROACH = [
  {
    title: "User-First Design",
    desc: "Every feature is shaped by real user needs and validated through prototyping before a single line of code is written.",
    icon: "ri-user-heart-line",
  },
  {
    title: "Performance Optimized",
    desc: "We obsess over battery usage, memory footprint, and startup time — so your app feels instant, even on older devices.",
    icon: "ri-battery-charge-line",
  },
  {
    title: "Privacy Built-In",
    desc: "User data stays on-device whenever possible. When cloud is needed, we implement end-to-end encryption by default.",
    icon: "ri-shield-check-line",
  },
  {
    title: "Continuous Iteration",
    desc: "Post-launch analytics drive our roadmap. We ship updates weekly to keep products competitive and users engaged.",
    icon: "ri-loop-right-line",
  },
];

export default function ProductsPage() {
  return (
    <div className="min-h-screen bg-cream-50 font-sans text-ink-950">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20and%20laptop%20devices%20displaying%20clean%20utility%20app%20interfaces%20on%20minimal%20desk%20setup%2C%20warm%20ambient%20lighting%2C%20professional%20product%20photography%2C%20cream%20and%20amber%20warm%20tones%2C%20high%20detail%2C%20sophisticated%20tech%20aesthetic%2C%20no%20text&width=1600&height=900&seq=products-hero-01&orientation=landscape"
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink-950/70 via-ink-950/50 to-ink-950/30"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-amber-200">
                  Our Products
                </span>
              </div>
              <h1 className="font-display font-bold text-cream-50 leading-[1.08] tracking-tight text-4xl md:text-5xl lg:text-6xl">
                Utility apps built for real-world performance
              </h1>
              <p className="mt-5 text-sm md:text-base text-cream-50/80 leading-relaxed max-w-lg">
                Each product is developed with a focus on speed, reliability, and user experience. We build tools people actually want to use every day.
              </p>
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="relative w-full bg-cream-50 py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="space-y-16 md:space-y-20">
              {PRODUCT_CATEGORIES.map((cat, i) => {
                const isReversed = i % 2 === 1;
                return (
                  <div key={cat.title} className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
                    <div className={`lg:col-span-2 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                      <div className="relative rounded-2xl overflow-hidden border border-ink-950/10">
                        <div className="relative aspect-[4/3] w-full">
                          <img
                            src={cat.image}
                            alt={cat.title}
                            className="absolute inset-0 w-full h-full object-cover object-center"
                          />
                        </div>
                      </div>
                    </div>
                    <div className={`lg:col-span-3 ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-moss-100">
                          <i className={`${cat.icon} text-xl text-moss-600`}></i>
                        </span>
                        <div>
                          <h3 className="font-display font-bold text-2xl md:text-3xl text-ink-950">{cat.title}</h3>
                          <p className="text-xs uppercase tracking-widest text-ink-500 font-medium mt-0.5">{cat.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-sm md:text-base text-ink-700 leading-relaxed mt-4">
                        {cat.desc}
                      </p>
                      <div className="mt-6 flex flex-wrap gap-2">
                        {cat.highlights.map((h) => (
                          <span
                            key={h}
                            className="inline-flex items-center px-3 py-1.5 rounded-full bg-cream-100 border border-ink-950/5 text-xs font-medium text-ink-700"
                          >
                            {h}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Approach */}
        <section className="relative w-full bg-cream-100 py-20 md:py-28 border-y border-ink-950/5">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-moss-500"></span>
                <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-ink-700">
                  Our Approach
                </span>
              </div>
              <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl">
                How we build products that win
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {APPROACH.map((item) => (
                <div key={item.title} className="group bg-cream-50 rounded-2xl border border-ink-950/5 p-7 md:p-8 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-amber-100 mb-5 group-hover:bg-amber-200 transition-colors">
                    <i className={`${item.icon} text-xl text-amber-600`}></i>
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-ink-700 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full bg-cream-50 py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14 text-center">
            <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl">
              Have a product idea?
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-sm md:text-base text-ink-700 leading-relaxed">
              We can take it from concept to launch — and beyond. Let&rsquo;s discuss how Trevica can bring your utility app to life.
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-ink-950 px-7 py-3.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Discuss your project
                <i className="ri-arrow-right-line text-base"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}